// src/features/dashboard/hooks/useTimeAllocationChart.ts

import {
  useEffect,
  useState,
} from 'react';

import {
  ENV,
} from '../../../config/env';

import {
  mapTimeAllocationChartData,
  timeAllocationChartMockData,
} from '../mocks/timeAllocationChart.mock';

import {
  getTimeAllocationChart,
} from '../services/timeAllocationChartApi';

import type {
  TimeAllocationChartItem,
} from '../types/timeAllocationChart.types';

import {
  useManagerDate,
} from '../context/ManagerDateContext';

import {
  formatApiDate,
} from '../utils/date.utils';

export const useTimeAllocationChart = (
  ManagerEmployeeCode: string,
) => {

  /**
   * =========================================
   * MANAGER DATE CONTEXT
   * =========================================
   */
  const {
    fromDate,
    toDate,
  } = useManagerDate();

  /**
   * =========================================
   * STATE
   * =========================================
   */
  const [
    chartData,
    setChartData,
  ] = useState<
    TimeAllocationChartItem[]
  >([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  /**
   * =========================================
   * LOAD CHART
   * =========================================
   */
  useEffect(() => {

    const loadChart =
      async () => {

        if (
          !ManagerEmployeeCode ||
          Number.isNaN(
            ManagerEmployeeCode,
          )
        ) {
          return;
        }

        try {

          setLoading(true);

          setError('');

          /**
           * =========================================
           * MOCK MODE
           * =========================================
           */
          if (
            ENV.USE_MOCK
          ) {

            const formattedData =
              mapTimeAllocationChartData(
                timeAllocationChartMockData,
              );

            setChartData(
              formattedData,
            );

            return;
          }

          /**
           * =========================================
           * API PAYLOAD
           * =========================================
           */
          const payload = {

            ManagerEmployeeCode,

            fromDate:
              formatApiDate(
                fromDate,
              ),

            toDate:
              formatApiDate(
                toDate,
              ),
          };

          console.log(
            'Time Allocation Payload:',
            payload,
          );

          /**
           * =========================================
           * API CALL
           * =========================================
           */
          const response =
            await getTimeAllocationChart(
              payload,
            );

          /**
           * =========================================
           * MAP RESPONSE
           * =========================================
           */
          const formattedData =
            mapTimeAllocationChartData(
              response,
            );

          /**
           * =========================================
           * SET DATA
           * =========================================
           */
          setChartData(
            formattedData,
          );

        } catch (err) {

          console.error(
            'Time allocation chart error:',
            err,
          );

          setError(
            'Failed to load time allocation chart',
          );

          setChartData([]);

        } finally {

          setLoading(false);
        }
      };

    loadChart();

  }, [
    ManagerEmployeeCode,
    fromDate,
    toDate,
  ]);

  return {

    chartData,

    loading,

    error,
  };
};